import Router from "next/router";
import { isClerkAPIResponseError } from "@clerk/nextjs/errors";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import {
  type AsyncStorage,
  experimental_createPersister,
  type PersistedQuery,
} from "@tanstack/react-query-persist-client";
import { createTRPCClient, httpLink, splitLink, TRPCClientError } from "@trpc/client";
import { createTRPCNext } from "@trpc/next";
import { del, get, set } from "idb-keyval";
import superjson from "superjson";
import type { AppRouter, RouterInputs } from "@remi/api";
import { API_VERSION, MFA_REQUIRED_HEADER } from "@remi/constants";
import { getEnv } from "@remi/environment";
import { remiLogger } from "@remi/logging";

import { getCurrentAdminTool } from "~/contexts/AdminToolContext";
import { guardTrpcResponse } from "./resilient-fetch";

declare global {
  interface Window {
    Clerk?: {
      session?: {
        reload: () => Promise<{
          getToken: () => Promise<string>;
        }>;
        getToken: () => Promise<string>;
      };
    };
  }
}

/** Silently ignore some errors due to browser IndexDB errors. */
async function handleIdbError<T>(fn: () => Promise<T>) {
  try {
    return await fn();
  } catch (error) {
    if (error == null) {
      // idb-keyval rejects with `request.error` directly, which the browser sets to null in
      // some abort/quota states (notably iOS Safari after long idle).
      remiLogger.captureCounterMetric("idb.null-error", 1);
      return;
    }
    if (error instanceof DOMException) {
      switch (error.name) {
        case "InvalidStateError":
          remiLogger.captureCounterMetric("idb.invalid-state-error", 1);
          return;
        case "UnknownError":
          remiLogger.captureCounterMetric("idb.unknown-error", 1);
          return;
        case "QuotaExceededError":
          remiLogger.captureCounterMetric("idb.quota-exceeded-error", 1);
          return;
        case "DataCloneError":
          remiLogger.captureCounterMetric("idb.data-clone-error", 1);
          return;
      }
    }
    throw error;
  }
}

function createIdbStorage(): AsyncStorage<PersistedQuery> {
  return {
    getItem: async (key) => {
      const result = await handleIdbError(() => get(key));
      return result ?? null;
    },
    setItem: async (key, value) => handleIdbError(() => set(key, value)),
    removeItem: async (key) => handleIdbError(() => del(key)),
  };
}

const getBaseUrl = () => {
  if (typeof window !== "undefined") {
    return ""; // browser should use relative url
  }
  if (getEnv("VERCEL_URL")) {
    return `https://${getEnv("VERCEL_URL")}`; // SSR should use vercel url
  }

  return getEnv("APP_URL");
};

/**
 * Safely retrieves Clerk token with proper error handling.
 *  - Clerk sent a 429 error without a message, so we use the status code to get the message
 */
export const getClerkToken = async (): Promise<string | null> => {
  try {
    return (await window.Clerk?.session?.getToken()) ?? null;
  } catch (error) {
    if (!isClerkAPIResponseError(error)) {
      throw error;
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const message = error.message ?? error.errors?.[0]?.message ?? error.status ?? "Authentication error";
    Object.defineProperty(error, "message", { value: message, writable: true });

    throw error;
  }
};

const isMfaRequiredError = (error: unknown): boolean => {
  if (!(error instanceof TRPCClientError)) {
    return false;
  }
  const headerValue =
    error.meta?.response instanceof Response ? error.meta.response.headers.get(MFA_REQUIRED_HEADER) : undefined;
  return headerValue === "true";
};

const handleMfaRequiredError = (error: unknown) => {
  if (typeof window === "undefined") {
    return;
  }
  if (window.location.pathname.startsWith("/mfa/setup")) {
    return;
  }
  if (isMfaRequiredError(error)) {
    window.location.href = "/mfa/setup";
  }
};

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleMfaRequiredError }),
  mutationCache: new MutationCache({ onError: handleMfaRequiredError }),
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      refetchOnWindowFocus: false,
      persister: experimental_createPersister<PersistedQuery>({
        buster: API_VERSION,
        // Without IndexedDB, idb-keyval throws a ReferenceError and the persister fails every query before it fetches.
        storage: typeof indexedDB === "undefined" ? null : createIdbStorage(),
        maxAge: 1000 * 60 * 60 * 24, // 24 hours
        serialize: (persistedQuery) => persistedQuery,
        deserialize: (cached) => cached,
      }),
    },
  },
});

const createHttpLink = (options?: { methodOverride?: "POST" }) =>
  httpLink({
    url: `${getBaseUrl()}/api/trpc`,
    transformer: superjson,
    methodOverride: options?.methodOverride,
    async fetch(input, init) {
      // tRPC's RequestInitEsque.body allows Uint8Array, which DOM's BodyInit rejects; copy it into a
      // new Uint8Array so the spread satisfies the stricter RequestInit type. httpLink+superjson only
      // ever sends a JSON string, so this branch is a type-only reconciliation that never runs.
      const rawBody = init?.body;
      const body = rawBody instanceof Uint8Array ? new Uint8Array(rawBody) : rawBody;
      const response = await fetch(input, init ? { ...init, body } : undefined);
      guardTrpcResponse(response, input);
      return response;
    },
    async headers() {
      const token = await getClerkToken();
      const adminTool = getCurrentAdminTool();
      // `.router` is null until Next mounts; the singleton's own `pathname` getter throws instead.
      const clientRoute = Router.router?.pathname;
      return {
        ...(token && { authorization: `Bearer ${token}` }),
        ...(adminTool && { "x-admin-tool": adminTool }),
        ...(clientRoute && { "x-client-route": clientRoute }),
      };
    },
  });

// Inputs too large for a GET: inboxCounts carries every daily-actions definition tree, getServiceAreaGeometries and
// getTotalsForContractors an entry per active contractor. Superjson-encoded plus the Clerk bearer header they exceed Vercel's request-line
// and header budget (414/431). The template types make a procedure rename a compile error, not a silent GET.
type CommunicationPath = `communication.${keyof RouterInputs["communication"] & string}`;
type ContractorAssignmentPath = `contractorAssignment.${keyof RouterInputs["contractorAssignment"] & string}`;
type WorkOrderPath = `workOrder.${keyof RouterInputs["workOrder"] & string}`;

const POST_OVERRIDE_PATHS: ReadonlySet<string> = new Set<CommunicationPath | ContractorAssignmentPath | WorkOrderPath>([
  "communication.inboxCounts",
  "contractorAssignment.getServiceAreaGeometries",
  "workOrder.getTotalsForContractors",
]);

const createLinkChain = () => [
  splitLink({
    condition: (op) => POST_OVERRIDE_PATHS.has(op.path),
    true: createHttpLink({ methodOverride: "POST" }),
    false: createHttpLink(),
  }),
];

export const api = createTRPCNext<AppRouter>({
  config: () => ({ queryClient, links: createLinkChain() }),
  transformer: superjson,
  ssr: false,
});

export const proxyApi = createTRPCClient<AppRouter>({ links: createLinkChain() });

export const apiVersion = API_VERSION;
